import React from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, CalendarCheck, ShieldCheck, Sparkles, CheckCircle2, TrendingUp, DollarSign, Unlock } from "lucide-react";
import AdvisorCard from "../components/AdvisorCard";

const tone = {
    Low: "bg-emerald-50 border-emerald-200 text-emerald-800",
    Medium: "bg-amber-50 border-amber-200 text-amber-800",
    High: "bg-rose-50 border-rose-200 text-rose-800",
    Strong: "bg-emerald-50 border-emerald-200 text-emerald-800",
    Moderate: "bg-amber-50 border-amber-200 text-amber-800",
    Weak: "bg-rose-50 border-rose-200 text-rose-800",
    Unknown: "bg-slate-50 border-slate-200 text-slate-700",
};

const barWidth = (label) => {
    const map = { Low: "w-1/3", Medium: "w-2/3", High: "w-full", Weak: "w-1/3", Moderate: "w-2/3", Strong: "w-full", Unknown: "w-1/4" };
    return map[label] || "w-1/2";
};

const barColor = (label) => {
    const map = {
        Low: "bg-gradient-to-r from-emerald-400 to-emerald-600",
        Medium: "bg-gradient-to-r from-amber-400 to-amber-600",
        High: "bg-gradient-to-r from-rose-400 to-rose-600",
        Weak: "bg-gradient-to-r from-rose-400 to-rose-600",
        Moderate: "bg-gradient-to-r from-amber-400 to-amber-600",
        Strong: "bg-gradient-to-r from-emerald-400 to-emerald-600",
        Unknown: "bg-slate-400",
    };
    return map[label] || "bg-slate-400";
};

const cardTones = {
    Fees: { ring: "ring-rose-100", icon: "bg-rose-100 text-rose-700", strip: "bg-rose-500" },
    Flexibility: { ring: "ring-amber-100", icon: "bg-amber-100 text-amber-700", strip: "bg-amber-500" },
    "Income Strength": { ring: "ring-emerald-100", icon: "bg-emerald-100 text-emerald-700", strip: "bg-emerald-500" },
};

const cardIcons = {
    Fees: DollarSign,
    Flexibility: Unlock,
    "Income Strength": TrendingUp,
};

const interpret = {
    fees: {
        Low: "Your contract type generally carries lower internal cost — a positive starting point.",
        Medium: "Fixed indexed annuities have moderate internal costs that vary based on caps, spreads, and rider charges.",
        High: "Variable annuity fees can be among the highest in the industry. A full review is strongly recommended.",
    },
    flexibility: {
        Low: "Knowing your surrender schedule is good — but if you're early in the period, your liquidity may be limited.",
        Medium: "Most owners are unsure of their schedule. We can help you confirm your exact terms.",
        High: "Not having a surrender schedule typically means full liquidity — generally a positive feature.",
    },
    income_strength: {
        Strong: "An income rider can provide a guaranteed lifetime income — a powerful planning tool when used correctly.",
        Moderate: "Without an income rider, your annuity may still provide income through annuitization or withdrawals.",
        Unknown: "We'll help you confirm whether your contract has an income rider and what it actually guarantees.",
    },
};

export default function Results() {
    const location = useLocation();
    const data = location.state;
    if (!data?.scorecard) return <Navigate to="/review" replace />;

    const { fees, flexibility, income_strength } = data.scorecard;

    const cards = [
        { label: "Fees", value: fees, copy: interpret.fees[fees] },
        { label: "Flexibility", value: flexibility, copy: interpret.flexibility[flexibility] },
        { label: "Income Strength", value: income_strength, copy: interpret.income_strength[income_strength] },
    ];

    return (
        <div className="relative overflow-hidden bg-navy-900 min-h-screen py-16 md:py-24" data-testid="results-page">
            {/* Decorative background */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-[#0B2F3A] to-emerald-900/70" />
                <div className="absolute -top-32 -right-40 w-[560px] h-[560px] rounded-full bg-amber-400/10 blur-3xl" />
                <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-emerald-400/10 blur-3xl" />
            </div>

            <div className="relative max-w-5xl mx-auto px-6 sm:px-8">
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-2 uppercase tracking-[0.25em] text-[11px] font-bold text-amber-300 mb-5 bg-amber-400/10 border border-amber-400/30 rounded-full px-4 py-1.5">
                        <Sparkles className="w-3 h-3" /> Your Annuity Scorecard
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
                        Thanks{data.name ? <>, <span className="text-amber-300">{data.name.split(" ")[0]}</span></> : ""} —<br className="hidden sm:block" /> here's a quick look.
                    </h1>
                    <p className="mt-5 text-lg text-blue-50/85">
                        This is a simplified snapshot based on what you shared. A full review will provide a detailed breakdown of your specific contract.
                    </p>
                </div>

                {/* Confirmation chip */}
                <div className="mt-8 max-w-2xl mx-auto bg-emerald-500/10 border border-emerald-400/30 rounded-xl p-4 flex items-start gap-3 text-blue-50/95">
                    <CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <strong className="text-white">Confirmation email sent</strong> — check your inbox for your booking link and what to expect on the call.
                    </div>
                </div>

                {/* Scorecard grid */}
                <div className="mt-12 grid md:grid-cols-3 gap-6">
                    {cards.map((c) => {
                        const t = cardTones[c.label] || { ring: "ring-slate-100", icon: "bg-slate-100 text-slate-700", strip: "bg-slate-500" };
                        const Icon = cardIcons[c.label];
                        return (
                            <div
                                key={c.label}
                                data-testid={`scorecard-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`relative bg-white border border-slate-200 rounded-2xl p-8 shadow-xl ring-1 ${t.ring} hover:-translate-y-1 transition-all overflow-hidden`}
                            >
                                <span className={`absolute top-0 left-0 right-0 h-1 ${t.strip}`} />
                                <div className="flex items-center justify-between">
                                    <div className="uppercase tracking-[0.2em] text-[11px] font-bold text-slate-700">{c.label}</div>
                                    <div className={`w-9 h-9 rounded-md flex items-center justify-center ${t.icon}`}>
                                        {Icon && <Icon className="w-4 h-4" />}
                                    </div>
                                </div>
                                <div className={`mt-4 inline-flex items-center px-3 py-1.5 rounded-md border text-sm font-bold ${tone[c.value] || tone.Unknown}`}>
                                    {c.value}
                                </div>
                                <div className="mt-6 h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div className={`h-full ${barWidth(c.value)} ${barColor(c.value)} transition-all`} />
                                </div>
                                <p className="mt-6 text-base text-slate-600 leading-relaxed">{c.copy}</p>
                            </div>
                        );
                    })}
                </div>

                {/* Your assigned advisor */}
                {data.advisor && data.advisor.name && (
                    <div className="mt-12 max-w-3xl mx-auto">
                        <AdvisorCard advisor={data.advisor} />
                    </div>
                )}

                {/* Cross-sell: Get the Retirement Readiness Scorecard™ */}
                <div className="mt-10 max-w-3xl mx-auto bg-gradient-to-br from-emerald-900 to-emerald-700 text-emerald-50 rounded-2xl p-7 sm:p-9 shadow-xl" data-testid="annuity-to-wealth-crosslink">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-amber-300 font-bold">Want the full picture?</div>
                    <h3 className="mt-3 font-serif text-2xl text-white font-semibold leading-tight">
                        Get your Retirement Readiness Scorecard&trade;
                    </h3>
                    <p className="mt-3 text-emerald-100/85 text-sm leading-relaxed max-w-xl">
                        A 5-minute assessment scored across income, growth, market protection, and flexibility. Instant PDF-style report — separate from this annuity scorecard.
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 text-xs text-amber-300 font-semibold">
                        <span className="line-through text-emerald-100/40 font-sans">$49.95</span>
                        <span>Launch Special · $29.95</span>
                    </div>
                    <a
                        href="/wealth"
                        data-testid="annuity-to-wealth-cta"
                        className="mt-5 inline-flex items-center gap-2 bg-amber-300 hover:bg-amber-200 text-emerald-950 px-5 py-3 rounded-lg font-bold text-sm shadow"
                    >
                        Get my Scorecard&trade; → $29.95
                    </a>
                </div>

                {/* Final CTA */}
                <div className="mt-14 relative bg-gradient-to-br from-navy-900 via-navy-900 to-emerald-900 text-white rounded-2xl p-10 md:p-14 text-center border border-amber-400/20 shadow-2xl overflow-hidden">
                    <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-amber-400/10 blur-3xl" />
                    <div className="relative">
                        <div className="inline-flex w-14 h-14 rounded-xl bg-amber-400/15 border border-amber-400/30 items-center justify-center text-amber-300">
                            <ShieldCheck className="w-7 h-7" />
                        </div>
                        <h2 className="mt-4 font-serif text-3xl sm:text-4xl">Book Your Free Review Call</h2>
                        <p className="mt-4 text-blue-100/85 text-lg max-w-2xl mx-auto">
                            {data.advisor?.name
                                ? `A 15-minute, no-pressure call with ${data.advisor.name.split(" ")[0]} to walk through your scorecard.`
                                : "A 15-minute, no-pressure call to walk through your scorecard and answer your questions."}
                        </p>
                        <div className="mt-8">
                            <Link
                                to="/booking"
                                state={{ leadId: data.leadId, name: data.name, email: data.email, phone: data.phone, advisor: data.advisor }}
                                data-testid="results-cta-book"
                            >
                                <Button className="bg-amber-400 text-slate-900 hover:bg-amber-300 hover:-translate-y-0.5 transition-all px-8 py-7 text-base font-bold rounded-md shadow-lg shadow-amber-500/25">
                                    <CalendarCheck className="w-5 h-5 mr-2" /> Book My Review Call
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                        </div>
                        <div className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-2 text-xs text-blue-100/75">
                            <span>✓ No fees</span>
                            <span>✓ No follow-up sales calls</span>
                            <span>✓ Written summary you keep</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
